/**
 * The property-management **operations** route table (C9 §5.5).
 *
 * `src/router/index.ts` is shared by every module and belongs to the wiring
 * packet, so this file declares the records and the router spreads
 * `PMS_OPS_ROUTES` in beside P12's `PMS_ROUTES` and P13's
 * `PMS_HOUSEKEEPING_ROUTES`. Three tables rather than one because §13 splits
 * `web/src/modules/pms/**` across three packets by screen: folding them
 * together would make whichever packet landed second the owner of the others'
 * paths.
 *
 * Every `:id` segment carries the `\d+` constraint, so no literal word can ever
 * be parsed as a document id, and each family's list route is declared before
 * its detail route so the ordering says the same thing the constraint enforces.
 *
 * Which permission guards which screen follows the API rather than the topic.
 * Two are worth stating because the obvious guess is wrong:
 *
 *  - **The folio screen is `pms.folio.read`, not `pms.frontdesk.read`.** An
 *    arrivals board shows a folio balance because the board endpoint returns
 *    one; opening the folio behind it is a separate authority. C9 §6.14's
 *    self-service trap is a room id pivoting to a folio, and this is the same
 *    pivot from the front-desk side.
 *  - **The night-audit board is `pms.night_audit.read`, and running it is
 *    `pms.night_audit.run`** — which `front_office_manager` deliberately does
 *    not hold. The board renders for a reader with no run affordance at all,
 *    because "the auditor checks the desk" only means anything if the desk
 *    cannot close its own night.
 */

import type { RouteRecordRaw } from 'vue-router'

import { PMS_OPS_PERMISSIONS } from '@/modules/pms/ops/permissions'

export const PMS_OPS_ROUTES: RouteRecordRaw[] = [
  {
    path: '/pms/front-desk',
    name: 'pms-front-desk',
    component: () => import('@/modules/pms/ops/FrontDeskView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.frontDesk',
      permission: PMS_OPS_PERMISSIONS.frontDeskRead,
    },
  },
  {
    path: '/pms/folios',
    name: 'pms-folios',
    component: () => import('@/modules/pms/ops/FoliosListView.vue'),
    meta: { titleKey: 'pms.ops.routeTitles.folios', permission: PMS_OPS_PERMISSIONS.folioRead },
  },
  {
    path: '/pms/folios/:id(\\d+)',
    name: 'pms-folio',
    component: () => import('@/modules/pms/ops/FolioView.vue'),
    meta: { titleKey: 'pms.ops.routeTitles.folio', permission: PMS_OPS_PERMISSIONS.folioRead },
  },
  {
    path: '/pms/deposits',
    name: 'pms-deposits',
    component: () => import('@/modules/pms/ops/DepositsListView.vue'),
    meta: { titleKey: 'pms.ops.routeTitles.deposits', permission: PMS_OPS_PERMISSIONS.folioRead },
  },
  {
    path: '/pms/night-audit',
    name: 'pms-night-audit',
    component: () => import('@/modules/pms/ops/NightAuditView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.nightAudit',
      permission: PMS_OPS_PERMISSIONS.nightAuditRead,
    },
  },
  {
    path: '/pms/night-audit/runs/:id(\\d+)',
    name: 'pms-night-audit-run',
    component: () => import('@/modules/pms/ops/NightAuditRunView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.nightAuditRun',
      permission: PMS_OPS_PERMISSIONS.nightAuditRead,
    },
  },
  {
    path: '/pms/channels',
    name: 'pms-channels',
    component: () => import('@/modules/pms/ops/ChannelsListView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.channels',
      permission: PMS_OPS_PERMISSIONS.channelRead,
    },
  },
  {
    path: '/pms/channels/:id(\\d+)',
    name: 'pms-channel',
    component: () => import('@/modules/pms/ops/ChannelView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.channel',
      permission: PMS_OPS_PERMISSIONS.channelRead,
    },
  },
  {
    path: '/pms/parity',
    name: 'pms-parity',
    component: () => import('@/modules/pms/ops/ParityBoardView.vue'),
    meta: { titleKey: 'pms.ops.routeTitles.parity', permission: PMS_OPS_PERMISSIONS.channelRead },
  },
  {
    path: '/pms/commission/agreements',
    name: 'pms-commission-agreements',
    component: () => import('@/modules/pms/ops/CommissionAgreementsView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.agreements',
      permission: PMS_OPS_PERMISSIONS.channelRead,
    },
  },
  {
    path: '/pms/commission/agreements/:id(\\d+)',
    name: 'pms-commission-agreement',
    component: () => import('@/modules/pms/ops/CommissionAgreementView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.agreement',
      permission: PMS_OPS_PERMISSIONS.channelRead,
    },
  },
  {
    path: '/pms/commission/accruals',
    name: 'pms-commission-accruals',
    component: () => import('@/modules/pms/ops/CommissionAccrualsView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.accruals',
      permission: PMS_OPS_PERMISSIONS.channelRead,
    },
  },
  {
    path: '/pms/commission/statements',
    name: 'pms-commission-statements',
    component: () => import('@/modules/pms/ops/CommissionStatementsView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.statements',
      permission: PMS_OPS_PERMISSIONS.channelRead,
    },
  },
  {
    path: '/pms/commission/statements/:id(\\d+)',
    name: 'pms-commission-statement',
    component: () => import('@/modules/pms/ops/CommissionStatementView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.statement',
      permission: PMS_OPS_PERMISSIONS.channelRead,
    },
  },
  {
    path: '/pms/services',
    name: 'pms-services',
    component: () => import('@/modules/pms/ops/ServicesListView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.services',
      permission: PMS_OPS_PERMISSIONS.serviceRead,
    },
  },
  {
    path: '/pms/services/schedule',
    name: 'pms-service-schedule',
    component: () => import('@/modules/pms/ops/ServiceScheduleView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.schedule',
      permission: PMS_OPS_PERMISSIONS.serviceRead,
    },
  },
  {
    path: '/pms/board-plans',
    name: 'pms-board-plans',
    component: () => import('@/modules/pms/ops/BoardPlansView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.boardPlans',
      permission: PMS_OPS_PERMISSIONS.serviceRead,
    },
  },
  {
    path: '/pms/board-allowances/unconsumed',
    name: 'pms-unconsumed-allowances',
    component: () => import('@/modules/pms/ops/UnconsumedAllowancesView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.unconsumed',
      permission: PMS_OPS_PERMISSIONS.reportRead,
    },
  },
  {
    // The five management reports (§6.12). `pms.report.read`, like the breakage
    // report above it: a controller's screen, not a guest's bill.
    path: '/pms/reports',
    name: 'pms-reports',
    component: () => import('@/modules/pms/ops/ReportsView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.reports',
      permission: PMS_OPS_PERMISSIONS.reportRead,
    },
  },
  {
    path: '/pms/transfers',
    name: 'pms-transfers',
    component: () => import('@/modules/pms/ops/TransferLegsView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.transfers',
      permission: PMS_OPS_PERMISSIONS.serviceRead,
    },
  },
  {
    path: '/pms/guest-requests',
    name: 'pms-guest-requests',
    component: () => import('@/modules/pms/ops/GuestRequestsView.vue'),
    meta: {
      titleKey: 'pms.ops.routeTitles.guestRequests',
      permission: PMS_OPS_PERMISSIONS.requestManage,
    },
  },
]

/** The same set as strings, for links inside the module. */
export const PMS_OPS_PATHS = {
  frontDesk: '/pms/front-desk',
  folios: '/pms/folios',
  folio: (id: number) => `/pms/folios/${id}`,
  deposits: '/pms/deposits',
  nightAudit: '/pms/night-audit',
  nightAuditRun: (id: number) => `/pms/night-audit/runs/${id}`,
  channels: '/pms/channels',
  channel: (id: number) => `/pms/channels/${id}`,
  parity: '/pms/parity',
  agreements: '/pms/commission/agreements',
  agreement: (id: number) => `/pms/commission/agreements/${id}`,
  accruals: '/pms/commission/accruals',
  statements: '/pms/commission/statements',
  statement: (id: number) => `/pms/commission/statements/${id}`,
  services: '/pms/services',
  schedule: '/pms/services/schedule',
  boardPlans: '/pms/board-plans',
  unconsumed: '/pms/board-allowances/unconsumed',
  reports: '/pms/reports',
  transfers: '/pms/transfers',
  guestRequests: '/pms/guest-requests',
} as const

/**
 * Deep links out of the module, to screens other packets own.
 *
 * `journal` is the night audit's drill-through: the run board shows the lines it
 * built and links to the entry it posted, so "the report and the ledger can
 * never diverge" is something a reader can check rather than something the
 * screen asserts.
 */
export const PMS_OPS_EXTERNAL_PATHS = {
  reservation: (id: number) => `/pms/reservations/${id}`,
  guest: (id: number) => `/pms/guests/${id}`,
  journal: (id: number) => `/gl/journal-entries/${id}`,
  arInvoice: (id: number) => `/ar/invoices/${id}`,
  queues: '/controls/queues',
  approvals: '/approvals',
} as const
